import { Component, OnInit } from "@angular/core";
import { MatOptionSelectionChange } from "@angular/material/core";
import { MatDialog } from "@angular/material/dialog";
import saveAs from "file-saver";
import { BehaviorSubject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { BillDTO } from "src/app/data/schema/Billing/Bills/BillDTO";
import { BillStatusDTO } from "src/app/data/schema/Billing/Bills/BillStatusDTO";
import { BillQueryParameterDTO } from "src/app/data/schema/Billing/Filter/BillQueryParameterDTO";
import { InvoiceFiltersDTO } from "src/app/data/schema/Invoice/InvoiceFiltersDTO";
import { ApiBillingBillsService } from "src/app/data/service/billing/api-billing-bills.service";
import { ApiInvoiceService } from "src/app/data/service/fiscal/api-invoice.service";
import { TranslationLoaderService } from "src/app/core/service/translation-loader.service";
import { CancelBillComponent } from "./components/cancel-bill/cancel-bill.component";
import { locale as english } from "./i18n/en-US";
import { locale as spanish } from "./i18n/es-ES";
import { locale as portuguese } from "./i18n/pt-BR";

@Component({
    selector: "app-profile-billing",
    templateUrl: "./profile-billing.component.html",
    styleUrls: ["./profile-billing.component.scss"]
})
export class ProfileBillingComponent implements OnInit {
    public loading: boolean;
    public showMore: boolean;

    private query: BillQueryParameterDTO;

    public status$: BehaviorSubject<Array<BillStatusDTO>>;

    public bills$: BehaviorSubject<Array<BillDTO>>;

    constructor(
        private api: ApiBillingBillsService,
        private apiInvoiceService: ApiInvoiceService,
        private _matDialog: MatDialog,
        private translationLoaderService: TranslationLoaderService
    ) {
        this.translationLoaderService.loadTranslations(english, portuguese, spanish);
        
        this.loading = true;
        this.showMore = true;
        this.status$ = new BehaviorSubject<Array<BillStatusDTO>>([]);
        this.bills$ = new BehaviorSubject<Array<BillDTO>>([]);
        this.query = {
            active: null,
            billingProvider: null,
            courseId: null,
            externalId: null,
            find: null,
            orderBy: null,
            planIds: [],
            sortOrder: null,
            skip: 0,
            take: 10,
            dueAt: null,
            paidAt: null,
            paymentMethod: [],
            status: [],
            userId: null
        };
    }

    ngOnInit(): void {
        this.status$.pipe(debounceTime(1500), distinctUntilChanged()).subscribe((status) => {
            this.query.skip = 0;
            this.query.status = status;
            this.bills$.next([]);
            this.load();
        });
    }

    openCancelBillDialog(bill: BillDTO): void {
        const dialogRef = this._matDialog.open(CancelBillComponent, {
            data: {
                bill: bill
            },
            maxHeight: "100vh",
            maxWidth: "100vw"
        });

        dialogRef.afterClosed().subscribe((result: boolean) => {
            if (result) {
                this.bills$.next([]);
                this.load();
            }
        });
    }

    public showStatus(bill: BillDTO): string {
        if (bill.status === "Paid") return "BILLED";
        if (bill.status === "ExternallyPaid") return "EXTERNALLY_PAID";
        if (bill.status === "Expired") return "EXPIRED";
        if (bill.status === "Canceled") return "CANCELED";
        if (bill.status === "Review") return "REVIEW";

        const now = new Date();

        if (bill.dueAt < new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0)) return "DUE";
        else return "PENDING";
    }

    public showMoney(value: number): string {
        if (value == null) value = 0;

        const integer = value.toFixed(2).toString().split(".")[0];
        const decimal = value.toFixed(2).toString().split(".")[1];

        if (integer.length <= 3) return `${integer},${decimal}`;
        else if (integer.length > 3 && integer.length <= 6)
            return `${integer.substr(0, integer.length - 3)}.${integer.substr(integer.length - 3, 3)},${decimal}`;
        else
            return `${integer.substr(0, integer.length - 6)}.${integer.substr(integer.length - 6, 3)}.${integer.substr(
                integer.length - 3,
                3
            )},${decimal}`;
    }

    private load(): void {
        this.loading = true;
        this.api.list(this.query).subscribe((bills) => {
            bills.data.forEach((bill) => {
                if (bill.status == BillStatusDTO.Paid || bill.status == BillStatusDTO.ExternallyPaid) {
                    const filter: InvoiceFiltersDTO = {
                        skip: 0,
                        take: 1,
                        billId: bill.id
                    };

                    this.apiInvoiceService.GetList(filter).subscribe((invoice) => (bill.invoice = invoice.data[0]));
                }
            });

            const temp = this.bills$.getValue();
            temp.push(...bills.data);
            this.bills$.next(temp);

            if (bills.totalRows > this.bills$.getValue().length) this.showMore = true;
            else this.showMore = false;

            this.loading = false;
        });
    }

    public loadMore(): void {
        this.query.skip += this.query.take;
        this.load();
    }

    public filterChanged(event: MatOptionSelectionChange): void {
        this.status$.next(event.source.value);
    }

    downloadInvoice(invoiceId: string): void {
        this.apiInvoiceService.GetPdf(invoiceId).subscribe((res) => {
            if (res) {
                const blob = new Blob([res], {
                    type: res.type
                });

                saveAs(blob, "Invoice");
            }
        });
    }
}
